import { LEGAL_POLICY_VERSION } from './policyVersion';

export const LEGAL_ACCEPTANCE_STORAGE_KEY = 'cryptoflow-legal-acceptance';
export const LEGAL_DOCS_VIEWED_STORAGE_KEY = 'cryptoflow-legal-docs-viewed';

export type LegalDocsViewedRecord = {
    version: string;
    privacy: boolean;
    terms: boolean;
};

export type LegalAcceptanceRecord = {
    version: string;
    acceptedAt: string;
};

export function readLegalAcceptance(): LegalAcceptanceRecord | null {
    try {
        const raw = localStorage.getItem(LEGAL_ACCEPTANCE_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as LegalAcceptanceRecord;
        if (!parsed?.version || !parsed?.acceptedAt) return null;
        return parsed;
    } catch {
        return null;
    }
}

export function hasAcceptedCurrentLegal(): boolean {
    const record = readLegalAcceptance();
    return record?.version === LEGAL_POLICY_VERSION;
}

export function recordLegalAcceptance(version: string = LEGAL_POLICY_VERSION): LegalAcceptanceRecord {
    const record: LegalAcceptanceRecord = {
        version,
        acceptedAt: new Date().toISOString(),
    };
    try {
        localStorage.setItem(LEGAL_ACCEPTANCE_STORAGE_KEY, JSON.stringify(record));
    } catch {
        /* quota / private mode — gate will keep asking */
    }
    return record;
}

export function readLegalDocsViewed(): LegalDocsViewedRecord | null {
    try {
        const raw = localStorage.getItem(LEGAL_DOCS_VIEWED_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as LegalDocsViewedRecord;
        if (parsed?.version !== LEGAL_POLICY_VERSION) return null;
        return parsed;
    } catch {
        return null;
    }
}

export function markLegalDocumentViewed(kind: 'privacy' | 'terms'): LegalDocsViewedRecord {
    const prev = readLegalDocsViewed() || { version: LEGAL_POLICY_VERSION, privacy: false, terms: false };
    const next: LegalDocsViewedRecord = {
        version: LEGAL_POLICY_VERSION,
        privacy: prev.privacy || kind === 'privacy',
        terms: prev.terms || kind === 'terms',
    };
    try {
        localStorage.setItem(LEGAL_DOCS_VIEWED_STORAGE_KEY, JSON.stringify(next));
    } catch {
        /* ignore */
    }
    return next;
}

export function hasViewedCurrentLegalDocuments(): boolean {
    const record = readLegalDocsViewed();
    return Boolean(record?.privacy && record?.terms);
}

export function clearLegalAcceptanceRecord(): void {
    try {
        localStorage.removeItem(LEGAL_ACCEPTANCE_STORAGE_KEY);
    } catch {
        /* ignore */
    }
}

export function clearLegalLocalState(): void {
    try {
        clearLegalAcceptanceRecord();
        localStorage.removeItem(LEGAL_DOCS_VIEWED_STORAGE_KEY);
    } catch {
        /* ignore */
    }
}

/** Hydrate or invalidate local acceptance from server profile. Stale client records must not survive a server miss. */
export function syncLegalAcceptanceFromProfile(version?: string | null, acceptedAt?: string | null): void {
    if (version === LEGAL_POLICY_VERSION && acceptedAt) {
        try {
            localStorage.setItem(
                LEGAL_ACCEPTANCE_STORAGE_KEY,
                JSON.stringify({ version, acceptedAt } satisfies LegalAcceptanceRecord),
            );
        } catch {
            /* ignore */
        }
        return;
    }
    clearLegalAcceptanceRecord();
}
